<template>
  <div
    class="portfolio-section"
    :id="id"
    v-if="numPages > 0"
  >
    <Pagination
      :current-page="currentPage"
      :num-pages="numPages"
      @onPageChange="updatePageContent"
    >
      <DocumentCardIterator :items="documents">
        <template slot="header">
          <h3 class="pa-3">
            {{ name }} ({{ numDocs }})
          </h3>
        </template>
        <template v-slot:item="{ item }" v-if="documents.length > 0">
          <template>
            <PortfolioCard
              :key="item.key"
              :item="item"
              :is-loading="isLoading"
            />
          </template>
        </template>
      </DocumentCardIterator>
    </Pagination>
  </div>
</template>

<script>
import { nanoid } from 'nanoid';
import { mapGetters } from 'vuex';
import { NoosAssistantSearch } from '@/components/search/SearchUtils';
import DocumentCardIterator from '@/components/cards/DocumentCardIterator';
import Pagination from '@/shared/components/pagination/Pagination';
import { types } from '../../../store/rootTypes';
import PortfolioCard from './PortfolioCard';

const NUM_DOCUMENTS = 8;

export default {
  name: 'PortfolioSection',
  components: {
    DocumentCardIterator,
    PortfolioCard,
    Pagination,
  },
  computed: {
    ...mapGetters({
      lastAclChangedEntityId: types.aclEditor.getters.lastAclChangedEntityId,
    }),
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    searchInput: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      documents: [],
      isLoading: true,
      currentPage: 0,
      numPages: 1,
      numDocs: 0,
    };
  },
  methods: {
    updatePageContent(page) {
      this.currentPage = page;
      this.updateDocuments();
    },
    resetDocuments() {
      // Reset all documents so that the loading animation works.
      let numDocuments = this.documents.length;
      if (numDocuments === 0) {
        numDocuments = NUM_DOCUMENTS;
      }
      this.documents = Array(numDocuments)
        .fill(undefined) // Mandatory to have a real array of length `numDocuments`
        .map(() => ({ documentDetails: {}, key: nanoid(8) }));
    },
    buildFetchQuery() {
      let offset = this.currentPage * NUM_DOCUMENTS - 1;

      if (offset < 0) {
        offset = 0;
      }

      const scorings = [
        {
          PortfolioAddedTime: {
            multiplier: 1.0,
            param: {
              kind: 'Linear',
              half_life: {
                amount: 1,
                unit: 'W',
              },
            },
          },
        },
        {
          PortfolioAddedTime: {
            multiplier: 1.0,
            param: {
              kind: 'Linear',
              half_life: {
                amount: 1,
                unit: 'M',
              },
            },
          },
        },
        {
          PortfolioAddedTime: {
            multiplier: 1.0,
            param: {
              kind: 'Linear',
              half_life: {
                amount: 1,
                unit: 'Y',
              },
            },
          },
        },
        {
          PortfolioAddedTime: {
            multiplier: 1.0,
            param: {
              kind: 'Linear',
              half_life: {
                amount: 10,
                unit: 'Y',
              },
            },
          },
        },
      ];
      const filters = [
        {
          PortfolioId: {
            kind: 'Include',
            param: this.id,
          },
        },
        {
          DocKind: {
            kind: 'Include',
            param: 'Analysis',
          },
        },
      ];

      if (this.searchInput && this.searchInput !== '') {
        scorings.push({
          Semantic: {
            multiplier: 5.0,
            param: this.searchInput,
          },
        });
        scorings.push({
          Tags: {
            multiplier: 5.0,
            param: this.searchInput,
          },
        });
        filters.push(
          {
            Keywords: {
              kind: 'Include',
              param: this.searchInput,
            },
          },
        );
      }

      return {
        scorings,
        filters,
        offset,
      };
    },
    async updateDocuments() {
      this.isLoading = true;
      this.resetDocuments();

      const assistant = new NoosAssistantSearch();
      const query = this.buildFetchQuery();
      assistant.fromQuery(query);
      const results = await assistant.fetchResults(NUM_DOCUMENTS);

      this.documents = results.items.map(result => ({
        key: nanoid(16),
        documentDetails: {
          ...result.document,
        },
      }));
      this.numDocs = results.total;
      this.numPages = results.total
        ? Math.max(Math.round(results.total / NUM_DOCUMENTS), 1)
        : 0;

      this.isLoading = false;
    },
  },
  watch: {
    searchInput() {
      this.currentPage = 0;
      this.updateDocuments();
    },
    async lastAclChangedEntityId() {
      await this.updateDocuments();
    },
  },
  created() {
    this.updateDocuments();
  },
};
</script>

<style scoped>
.portfolio-section {
  width: 100%;
}
</style>
